import { Schema } from "effect";
import { DesktopWorkspaceGrantIdSchema } from "./desktop-workspace";
import { ProjectIdSchema, WorkspaceIdSchema } from "./ids";
import { OFFICE_BROWSER_REVISION_MAX_BYTES } from "./office-content";
import { NonNegativeSafeIntegerSchema, PapersFlowBoundaryParseOptions } from "./standard";
import { utf8ByteLength } from "./utf8";

export const DESKTOP_FILESYSTEM_MAX_RELATIVE_PATH_BYTES = 4_096;
export const DESKTOP_FILESYSTEM_MAX_NAME_BYTES = 1_024;
export const DESKTOP_FILESYSTEM_MAX_DIRECTORY_ENTRIES = 10_000;
export const DESKTOP_FILESYSTEM_MAX_TEXT_BYTES = 50 * 1_024 * 1_024;
export const DESKTOP_FILESYSTEM_MAX_TEXT_PATCH_BYTES = 1 * 1_024 * 1_024;
export const DESKTOP_FILESYSTEM_MAX_FILE_BYTES = OFFICE_BROWSER_REVISION_MAX_BYTES;
export const DESKTOP_FILESYSTEM_MAX_ERROR_MESSAGE_BYTES = 240;
export const DESKTOP_FILESYSTEM_READ_FILE_BYTES_REQUEST_PROTOCOL_VERSION = 2;
export const DESKTOP_FILESYSTEM_READ_FILE_BYTES_PROTOCOL_VERSION = 1;
export const DESKTOP_FILESYSTEM_CANCEL_FILE_READ_PROTOCOL_VERSION = 1;
export const DESKTOP_FILESYSTEM_READ_FILE_BYTES_MAGIC = "PFRB";
export const DESKTOP_FILESYSTEM_READ_FILE_BYTES_HEADER_BYTES = 11;
export const DESKTOP_FILESYSTEM_FILE_READ_OPERATION_ID_PATTERN = /^dfr_[0-9a-f]{32}$/u;
export const DESKTOP_FILESYSTEM_TEXT_REVISION_ID_PATTERN = /^dtr_[0-9a-f]{32}$/u;
export const DESKTOP_FILESYSTEM_TEXT_PATCH_PROTOCOL_VERSION = 1;
export const DESKTOP_FILESYSTEM_TEXT_PATCH_CONFIRMATION = "apply-reviewed-local-text-patch";
export const DESKTOP_FILESYSTEM_TEXT_PATCH_APPLIED_EVENT_PROTOCOL_VERSION = 1;
export const DESKTOP_FILESYSTEM_WRITE_FILE_BYTES_PROTOCOL_VERSION = 1;
export const DESKTOP_FILESYSTEM_WRITE_FILE_BYTES_CONFIRMATION = "write-local-office-bytes";


const hasControlCharacter = (value: string): boolean => {
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f)) return true;
  }
  return false;
};

const isCanonicalRelativePath = (value: string, allowEmpty: boolean): boolean =>
  (allowEmpty || value.length > 0) &&
  !value.startsWith("/") &&
  !value.includes("\\") &&
  !hasControlCharacter(value) &&
  utf8ByteLength(value) <= DESKTOP_FILESYSTEM_MAX_RELATIVE_PATH_BYTES &&
  (value.length === 0 ||
    value.split("/").every((segment) => segment !== "" && segment !== "." && segment !== ".."));

const canonicalRelativePath = (allowEmpty: boolean) =>
  Schema.String.check(
    Schema.makeFilter(
      (value) =>
        isCanonicalRelativePath(value, allowEmpty) ||
        `must be ${allowEmpty ? "an empty or " : "a "}canonical relative filesystem path`,
    ),
  );

export const DesktopFilesystemDirectoryPathSchema = canonicalRelativePath(true);
export const DesktopFilesystemFilePathSchema = canonicalRelativePath(false);

/** Process-scoped cancellation capability for one exact local file read. */
export const DesktopFilesystemFileReadOperationIdSchema = Schema.String.check(
  Schema.makeFilter(
    (value) =>
      DESKTOP_FILESYSTEM_FILE_READ_OPERATION_ID_PATTERN.test(value) ||
      "must be a canonical desktop file-read operation identifier",
  ),
);

/** Process-scoped opaque revision for one exact approved-root text snapshot. */
export const DesktopFilesystemTextRevisionIdSchema = Schema.String.check(
  Schema.makeFilter(
    (value) =>
      DESKTOP_FILESYSTEM_TEXT_REVISION_ID_PATTERN.test(value) ||
      "must be a canonical desktop text revision identifier",
  ),
);

export const DesktopFilesystemEntryNameSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.makeFilter(
    (value) =>
      (!value.includes("/") &&
        !value.includes("\\") &&
        value !== "." &&
        value !== ".." &&
        !hasControlCharacter(value) &&
        utf8ByteLength(value) <= DESKTOP_FILESYSTEM_MAX_NAME_BYTES) ||
      `must be one canonical filesystem name of at most ${DESKTOP_FILESYSTEM_MAX_NAME_BYTES} UTF-8 bytes`,
  ),
);

export const DesktopFilesystemEntryKindSchema = Schema.Literals([
  "directory",
  "file",
  "symlink",
  "other",
]);

export const DesktopFilesystemEntrySchema = Schema.Struct({
  name: DesktopFilesystemEntryNameSchema,
  relativePath: DesktopFilesystemFilePathSchema,
  kind: DesktopFilesystemEntryKindSchema,
  sizeBytes: NonNegativeSafeIntegerSchema,
}).check(
  Schema.makeFilter(
    (entry) =>
      entry.relativePath.split("/").at(-1) === entry.name ||
      "entry name must equal the final relative-path segment",
  ),
  Schema.makeFilter(
    (entry) =>
      entry.kind === "file" || entry.sizeBytes === 0 || "non-file entries must report zero bytes",
  ),
);

export const DesktopFilesystemListDirectoryInputSchema = Schema.Struct({
  grantId: DesktopWorkspaceGrantIdSchema,
  relativePath: DesktopFilesystemDirectoryPathSchema,
});

function isImmediateChild(directoryPath: string, relativePath: string): boolean {
  const prefix = directoryPath.length === 0 ? "" : `${directoryPath}/`;
  if (!relativePath.startsWith(prefix)) return false;
  const child = relativePath.slice(prefix.length);
  return child.length > 0 && !child.includes("/");
}

export const DesktopFilesystemListDirectoryResultSchema = Schema.Struct({
  version: Schema.Literal(1),
  relativePath: DesktopFilesystemDirectoryPathSchema,
  entries: Schema.Array(DesktopFilesystemEntrySchema).check(
    Schema.isMaxLength(DESKTOP_FILESYSTEM_MAX_DIRECTORY_ENTRIES),
  ),
}).check(
  Schema.makeFilter((result) => {
    const paths = new Set<string>();
    for (const entry of result.entries) {
      if (!isImmediateChild(result.relativePath, entry.relativePath)) {
        return "every entry must be an immediate child of the requested directory";
      }
      if (paths.has(entry.relativePath)) return "directory entry paths must be unique";
      paths.add(entry.relativePath);
    }
    return true;
  }),
);

export const DesktopFilesystemReadTextFileInputSchema = Schema.Struct({
  grantId: DesktopWorkspaceGrantIdSchema,
  relativePath: DesktopFilesystemFilePathSchema,
});

export const DesktopFilesystemReadTextFileResultSchema = Schema.Struct({
  version: Schema.Literal(1),
  relativePath: DesktopFilesystemFilePathSchema,
  revisionId: DesktopFilesystemTextRevisionIdSchema,
  text: Schema.String.check(
    Schema.makeFilter(
      (value) =>
        utf8ByteLength(value) <= DESKTOP_FILESYSTEM_MAX_TEXT_BYTES ||
        `must contain at most ${DESKTOP_FILESYSTEM_MAX_TEXT_BYTES} UTF-8 bytes`,
    ),
  ),
});

export const DesktopFilesystemTextPatchSchema = Schema.Struct({
  startByte: NonNegativeSafeIntegerSchema.check(
    Schema.isLessThanOrEqualTo(DESKTOP_FILESYSTEM_MAX_TEXT_BYTES),
  ),
  endByte: NonNegativeSafeIntegerSchema.check(
    Schema.isLessThanOrEqualTo(DESKTOP_FILESYSTEM_MAX_TEXT_BYTES),
  ),
  replacement: Schema.String.check(
    Schema.makeFilter(
      (value) =>
        utf8ByteLength(value) <= DESKTOP_FILESYSTEM_MAX_TEXT_PATCH_BYTES ||
        `must contain at most ${DESKTOP_FILESYSTEM_MAX_TEXT_PATCH_BYTES} UTF-8 bytes`,
    ),
  ),
}).check(
  Schema.makeFilter(
    (patch) => patch.startByte <= patch.endByte || "startByte must not exceed endByte",
  ),
  Schema.makeFilter(
    (patch) =>
      patch.endByte - patch.startByte <= DESKTOP_FILESYSTEM_MAX_TEXT_PATCH_BYTES ||
      `must replace at most ${DESKTOP_FILESYSTEM_MAX_TEXT_PATCH_BYTES} UTF-8 bytes`,
  ),
);

export const DesktopFilesystemApplyTextPatchInputSchema = Schema.Struct({
  version: Schema.Literal(DESKTOP_FILESYSTEM_TEXT_PATCH_PROTOCOL_VERSION),
  grantId: DesktopWorkspaceGrantIdSchema,
  relativePath: DesktopFilesystemFilePathSchema,
  expectedRevisionId: DesktopFilesystemTextRevisionIdSchema,
  patch: DesktopFilesystemTextPatchSchema,
  confirmation: Schema.Literal(DESKTOP_FILESYSTEM_TEXT_PATCH_CONFIRMATION),
});

export const DesktopFilesystemApplyTextPatchResultSchema = Schema.Struct({
  version: Schema.Literal(DESKTOP_FILESYSTEM_TEXT_PATCH_PROTOCOL_VERSION),
  relativePath: DesktopFilesystemFilePathSchema,
  revisionId: DesktopFilesystemTextRevisionIdSchema,
  text: Schema.String.check(
    Schema.makeFilter(
      (value) =>
        utf8ByteLength(value) <= DESKTOP_FILESYSTEM_MAX_TEXT_BYTES ||
        `must contain at most ${DESKTOP_FILESYSTEM_MAX_TEXT_BYTES} UTF-8 bytes`,
    ),
  ),
});

export const DesktopFilesystemTextPatchAppliedEventSchema = Schema.Struct({
  version: Schema.Literal(DESKTOP_FILESYSTEM_TEXT_PATCH_APPLIED_EVENT_PROTOCOL_VERSION),
  durability: Schema.Literal("transient"),
  type: Schema.Literal("desktop.filesystem.text-patch-applied"),
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  target: Schema.Struct({
    kind: Schema.Literal("local"),
    grantId: DesktopWorkspaceGrantIdSchema,
  }),
  relativePath: DesktopFilesystemFilePathSchema,
  revisionId: DesktopFilesystemTextRevisionIdSchema,
});

export const DesktopFilesystemReadFileBytesInputSchema = Schema.Struct({
  version: Schema.Literal(DESKTOP_FILESYSTEM_READ_FILE_BYTES_REQUEST_PROTOCOL_VERSION),
  grantId: DesktopWorkspaceGrantIdSchema,
  relativePath: DesktopFilesystemFilePathSchema,
  operationId: DesktopFilesystemFileReadOperationIdSchema,
});

export const DesktopFilesystemCancelFileReadInputSchema = Schema.Struct({
  version: Schema.Literal(DESKTOP_FILESYSTEM_CANCEL_FILE_READ_PROTOCOL_VERSION),
  operationId: DesktopFilesystemFileReadOperationIdSchema,
});

export const DesktopFilesystemCancelFileReadResultSchema = Schema.Struct({
  version: Schema.Literal(DESKTOP_FILESYSTEM_CANCEL_FILE_READ_PROTOCOL_VERSION),
  operationId: DesktopFilesystemFileReadOperationIdSchema,
});

export const DesktopFilesystemReadFileBytesResultSchema = Schema.Struct({
  version: Schema.Literal(DESKTOP_FILESYSTEM_READ_FILE_BYTES_PROTOCOL_VERSION),
  relativePath: DesktopFilesystemFilePathSchema,
  byteSize: NonNegativeSafeIntegerSchema.check(
    Schema.isGreaterThanOrEqualTo(1),
    Schema.isLessThanOrEqualTo(DESKTOP_FILESYSTEM_MAX_FILE_BYTES),
  ),
  bytes: Schema.Uint8Array,
}).check(
  Schema.makeFilter(
    (result) =>
      result.byteSize === result.bytes.byteLength ||
      "byteSize must equal the decoded byte payload length",
  ),
);


export const DesktopFilesystemWriteFileBytesInputSchema = Schema.Struct({
  version: Schema.Literal(DESKTOP_FILESYSTEM_WRITE_FILE_BYTES_PROTOCOL_VERSION),
  grantId: DesktopWorkspaceGrantIdSchema,
  relativePath: DesktopFilesystemFilePathSchema,
  expectedByteSize: NonNegativeSafeIntegerSchema.check(
    Schema.isGreaterThanOrEqualTo(1),
    Schema.isLessThanOrEqualTo(DESKTOP_FILESYSTEM_MAX_FILE_BYTES),
  ),
  confirmation: Schema.Literal(DESKTOP_FILESYSTEM_WRITE_FILE_BYTES_CONFIRMATION),
});

export const DesktopFilesystemWriteFileBytesResultSchema = Schema.Struct({
  version: Schema.Literal(DESKTOP_FILESYSTEM_WRITE_FILE_BYTES_PROTOCOL_VERSION),
  relativePath: DesktopFilesystemFilePathSchema,
  byteSize: NonNegativeSafeIntegerSchema.check(
    Schema.isGreaterThanOrEqualTo(1),
    Schema.isLessThanOrEqualTo(DESKTOP_FILESYSTEM_MAX_FILE_BYTES),
  ),
});

export const DesktopFilesystemErrorCodeSchema = Schema.Literals([
  "approval-required",
  "cancelled",
  "directory-too-large",
  "durability-uncertain",
  "file-too-large",
  "invalid-input",
  "invalid-response",
  "io-failed",
  "not-regular-file",
  "operation-limit",
  "path-escape",
  "root-not-granted",
  "stale-revision",
  "unavailable",
  "unsupported-content",
  "unsupported-entry",
  "write-failed",
]);

export const DesktopFilesystemErrorPayloadSchema = Schema.Struct({
  code: DesktopFilesystemErrorCodeSchema,
  message: Schema.String.check(
    Schema.isMinLength(1),
    Schema.makeFilter(
      (value) =>
        (!hasControlCharacter(value) &&
          utf8ByteLength(value) <= DESKTOP_FILESYSTEM_MAX_ERROR_MESSAGE_BYTES) ||
        `must be a bounded filesystem error message`,
    ),
  ),
  retryable: Schema.Boolean,
});

export type DesktopFilesystemEntryKind = typeof DesktopFilesystemEntryKindSchema.Type;
export type DesktopFilesystemEntry = typeof DesktopFilesystemEntrySchema.Type;
export type DesktopFilesystemEntryEncoded = typeof DesktopFilesystemEntrySchema.Encoded;
export type DesktopFilesystemListDirectoryInput =
  typeof DesktopFilesystemListDirectoryInputSchema.Type;
export type DesktopFilesystemListDirectoryInputEncoded =
  typeof DesktopFilesystemListDirectoryInputSchema.Encoded;
export type DesktopFilesystemListDirectoryResult =
  typeof DesktopFilesystemListDirectoryResultSchema.Type;
export type DesktopFilesystemListDirectoryResultEncoded =
  typeof DesktopFilesystemListDirectoryResultSchema.Encoded;
export type DesktopFilesystemReadTextFileInput =
  typeof DesktopFilesystemReadTextFileInputSchema.Type;
export type DesktopFilesystemReadTextFileInputEncoded =
  typeof DesktopFilesystemReadTextFileInputSchema.Encoded;
export type DesktopFilesystemReadTextFileResult =
  typeof DesktopFilesystemReadTextFileResultSchema.Type;
export type DesktopFilesystemReadTextFileResultEncoded =
  typeof DesktopFilesystemReadTextFileResultSchema.Encoded;
export type DesktopFilesystemTextRevisionId = typeof DesktopFilesystemTextRevisionIdSchema.Type;
export type DesktopFilesystemTextPatch = typeof DesktopFilesystemTextPatchSchema.Type;
export type DesktopFilesystemTextPatchEncoded = typeof DesktopFilesystemTextPatchSchema.Encoded;
export type DesktopFilesystemApplyTextPatchInput =
  typeof DesktopFilesystemApplyTextPatchInputSchema.Type;
export type DesktopFilesystemApplyTextPatchInputEncoded =
  typeof DesktopFilesystemApplyTextPatchInputSchema.Encoded;
export type DesktopFilesystemApplyTextPatchResult =
  typeof DesktopFilesystemApplyTextPatchResultSchema.Type;
export type DesktopFilesystemApplyTextPatchResultEncoded =
  typeof DesktopFilesystemApplyTextPatchResultSchema.Encoded;
export type DesktopFilesystemTextPatchAppliedEvent =
  typeof DesktopFilesystemTextPatchAppliedEventSchema.Type;
export type DesktopFilesystemTextPatchAppliedEventEncoded =
  typeof DesktopFilesystemTextPatchAppliedEventSchema.Encoded;
export type DesktopFilesystemReadFileBytesInput =
  typeof DesktopFilesystemReadFileBytesInputSchema.Type;
export type DesktopFilesystemReadFileBytesInputEncoded =
  typeof DesktopFilesystemReadFileBytesInputSchema.Encoded;
export type DesktopFilesystemFileReadOperationId =
  typeof DesktopFilesystemFileReadOperationIdSchema.Type;
export type DesktopFilesystemCancelFileReadInput =
  typeof DesktopFilesystemCancelFileReadInputSchema.Type;
export type DesktopFilesystemCancelFileReadInputEncoded =
  typeof DesktopFilesystemCancelFileReadInputSchema.Encoded;
export type DesktopFilesystemCancelFileReadResult =
  typeof DesktopFilesystemCancelFileReadResultSchema.Type;
export type DesktopFilesystemCancelFileReadResultEncoded =
  typeof DesktopFilesystemCancelFileReadResultSchema.Encoded;
export type DesktopFilesystemReadFileBytesResult =
  typeof DesktopFilesystemReadFileBytesResultSchema.Type;
export type DesktopFilesystemReadFileBytesResultEncoded =
  typeof DesktopFilesystemReadFileBytesResultSchema.Encoded;
export type DesktopFilesystemWriteFileBytesInput =
  typeof DesktopFilesystemWriteFileBytesInputSchema.Type;
export type DesktopFilesystemWriteFileBytesInputEncoded =
  typeof DesktopFilesystemWriteFileBytesInputSchema.Encoded;
export type DesktopFilesystemWriteFileBytesResult =
  typeof DesktopFilesystemWriteFileBytesResultSchema.Type;
export type DesktopFilesystemWriteFileBytesResultEncoded =
  typeof DesktopFilesystemWriteFileBytesResultSchema.Encoded;
export type DesktopFilesystemErrorCode = typeof DesktopFilesystemErrorCodeSchema.Type;
export type DesktopFilesystemErrorPayload = typeof DesktopFilesystemErrorPayloadSchema.Type;
export type DesktopFilesystemErrorPayloadEncoded =
  typeof DesktopFilesystemErrorPayloadSchema.Encoded;

export const decodeDesktopFilesystemListDirectoryInput = Schema.decodeUnknownSync(
  DesktopFilesystemListDirectoryInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopFilesystemListDirectoryInput = Schema.encodeSync(
  DesktopFilesystemListDirectoryInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopFilesystemListDirectoryResult = Schema.decodeUnknownSync(
  DesktopFilesystemListDirectoryResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopFilesystemListDirectoryResult = Schema.encodeSync(
  DesktopFilesystemListDirectoryResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopFilesystemReadTextFileInput = Schema.decodeUnknownSync(
  DesktopFilesystemReadTextFileInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopFilesystemReadTextFileInput = Schema.encodeSync(
  DesktopFilesystemReadTextFileInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopFilesystemReadTextFileResult = Schema.decodeUnknownSync(
  DesktopFilesystemReadTextFileResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopFilesystemReadTextFileResult = Schema.encodeSync(
  DesktopFilesystemReadTextFileResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopFilesystemApplyTextPatchInput = Schema.decodeUnknownSync(
  DesktopFilesystemApplyTextPatchInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopFilesystemApplyTextPatchInput = Schema.encodeSync(
  DesktopFilesystemApplyTextPatchInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopFilesystemApplyTextPatchResult = Schema.decodeUnknownSync(
  DesktopFilesystemApplyTextPatchResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopFilesystemApplyTextPatchResult = Schema.encodeSync(
  DesktopFilesystemApplyTextPatchResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopFilesystemTextPatchAppliedEvent = Schema.decodeUnknownSync(
  DesktopFilesystemTextPatchAppliedEventSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopFilesystemTextPatchAppliedEvent = Schema.encodeSync(
  DesktopFilesystemTextPatchAppliedEventSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopFilesystemReadFileBytesInput = Schema.decodeUnknownSync(
  DesktopFilesystemReadFileBytesInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopFilesystemReadFileBytesInput = Schema.encodeSync(
  DesktopFilesystemReadFileBytesInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopFilesystemCancelFileReadInput = Schema.decodeUnknownSync(
  DesktopFilesystemCancelFileReadInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopFilesystemCancelFileReadInput = Schema.encodeSync(
  DesktopFilesystemCancelFileReadInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopFilesystemCancelFileReadResult = Schema.decodeUnknownSync(
  DesktopFilesystemCancelFileReadResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopFilesystemCancelFileReadResult = Schema.encodeSync(
  DesktopFilesystemCancelFileReadResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopFilesystemReadFileBytesResult = Schema.decodeUnknownSync(
  DesktopFilesystemReadFileBytesResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopFilesystemReadFileBytesResult = Schema.encodeSync(
  DesktopFilesystemReadFileBytesResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopFilesystemWriteFileBytesInput = Schema.decodeUnknownSync(
  DesktopFilesystemWriteFileBytesInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopFilesystemWriteFileBytesInput = Schema.encodeSync(
  DesktopFilesystemWriteFileBytesInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopFilesystemWriteFileBytesResult = Schema.decodeUnknownSync(
  DesktopFilesystemWriteFileBytesResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopFilesystemWriteFileBytesResult = Schema.encodeSync(
  DesktopFilesystemWriteFileBytesResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopFilesystemErrorPayload = Schema.decodeUnknownSync(
  DesktopFilesystemErrorPayloadSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopFilesystemErrorPayload = Schema.encodeSync(
  DesktopFilesystemErrorPayloadSchema,
  PapersFlowBoundaryParseOptions,
);
